/**
 * Reports (pivot doc §5, §6 Stage 4): saved reports over the same demo
 * figures Overview and Analytics already show, a schedule to run them on a
 * cadence, and exports — async in shape (queued → ready → expired) but
 * generated, not produced by a real export pipeline.
 *
 * A report's rows are never invented for this feature: each dataset reuses
 * the exact builder Overview/Analytics or Automation's own activity feed
 * uses, so a number in a report always agrees with the page it came from.
 */

import type { ValueFormat } from '@/design/format'
import type { GlobalFilters } from '@/lib/filters'

import type { Envelope } from './types'

// --- datasets --------------------------------------------------------------

export const REPORT_DATASETS = ['competitions', 'channels', 'automation_activity'] as const
export type ReportDataset = (typeof REPORT_DATASETS)[number]

export const REPORT_DATASET_LABELS: Record<ReportDataset, string> = {
  competitions: 'Competition performance',
  channels: 'Channel performance',
  automation_activity: 'Automation activity',
}

export interface ReportColumnDef {
  key: string
  header: string
  align?: 'start' | 'end'
  /** Set for a numeric column, so a screen can format it the way every other figure is formatted. */
  format?: ValueFormat
}

export const REPORT_DATASET_COLUMNS: Record<ReportDataset, readonly ReportColumnDef[]> = {
  competitions: [
    { key: 'name', header: 'Competition' },
    { key: 'category', header: 'Category' },
    { key: 'revenue', header: 'Revenue', align: 'end', format: 'currency' },
    { key: 'ticketsSold', header: 'Tickets sold', align: 'end', format: 'integer' },
    { key: 'sellThrough', header: 'Sell-through', align: 'end', format: 'percent' },
    { key: 'pacing', header: 'Pacing' },
    { key: 'closesOn', header: 'Closes on' },
  ],
  channels: [
    { key: 'name', header: 'Channel' },
    { key: 'kind', header: 'Kind' },
    { key: 'spend', header: 'Spend', align: 'end', format: 'currency' },
    { key: 'attributedRevenue', header: 'Attributed revenue', align: 'end', format: 'currency' },
    { key: 'orders', header: 'Orders', align: 'end', format: 'integer' },
    { key: 'roas', header: 'ROAS', align: 'end', format: 'ratio' },
  ],
  automation_activity: [
    { key: 'workflowName', header: 'Workflow' },
    { key: 'at', header: 'When' },
    { key: 'outcome', header: 'Outcome' },
    { key: 'summary', header: 'Summary' },
  ],
}

/** One report row, already formatted for display and for a CSV cell alike. */
export type ReportRow = Record<string, string | number>

// --- saved reports -----------------------------------------------------------

export const REPORT_CADENCES = ['daily', 'weekly', 'monthly'] as const
export type ReportCadence = (typeof REPORT_CADENCES)[number]

export const REPORT_CADENCE_LABELS: Record<ReportCadence, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
}

export const REPORT_FORMATS = ['csv', 'pdf'] as const
export type ReportFormat = (typeof REPORT_FORMATS)[number]

export interface ReportSchedule {
  cadence: ReportCadence
  recipients: string[]
  format: ReportFormat
  nextRunAt: string
}

export interface SavedReport {
  id: string
  name: string
  dataset: ReportDataset
  /** The column keys shown, in order — a subset of `REPORT_DATASET_COLUMNS[dataset]`. */
  columns: string[]
  owner: string
  createdAt: string
  updatedAt: string
  schedule: ReportSchedule | null
}

export interface ReportInput {
  name: string
  dataset: ReportDataset
  columns: string[]
}

// --- exports -------------------------------------------------------------------

export const EXPORT_STATES = ['queued', 'ready', 'expired'] as const
export type ExportState = (typeof EXPORT_STATES)[number]

export interface ReportExport {
  id: string
  reportId: string | null
  reportName: string
  dataset: ReportDataset
  format: ReportFormat
  requestedAt: string
  requestedBy: string
  state: ExportState
  expiresAt: string | null
  /** A placeholder for the signed download link a real export pipeline would issue. */
  downloadHint: string
}

export interface RequestExportInput {
  reportId: string | null
  dataset: ReportDataset
  format: ReportFormat
}

// --- the API slice --------------------------------------------------------------

export interface ReportsApi {
  savedReports(): Promise<Envelope<SavedReport[]>>
  savedReport(id: string): Promise<Envelope<SavedReport | null>>
  createSavedReport(input: ReportInput): Promise<Envelope<SavedReport>>
  duplicateSavedReport(id: string): Promise<Envelope<SavedReport>>
  scheduleSavedReport(id: string, schedule: ReportSchedule | null): Promise<Envelope<SavedReport>>
  reportRows(dataset: ReportDataset, filters: GlobalFilters): Promise<Envelope<ReportRow[]>>
  exports(): Promise<Envelope<ReportExport[]>>
  requestExport(input: RequestExportInput): Promise<Envelope<ReportExport>>
}
